import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled from 'styled-components';
import './Calendar.css';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import LoanInfoService from '../../../../service/LoanInfo';
import { isLogin, userGradeType } from '../../../../utils/auth';
import { useModal } from '../../../../context/ModalContext';

interface calendarDataProps {
  b_date: string;
  cnt: string;
}

interface curCalendarDataProps {
  courtname: string;
  courtname_abbr: string;
  cnt: string;
}

interface IProps {
  page: string;
  court: string;
  setCourt: (value: string) => void;
  tabCategory?: string;
  calendarData: calendarDataProps[];
  curCalendarData: curCalendarDataProps[];
  selectedDate: string;
  setSelectedDate: (value: string) => void;
}

const AuctionCalendar = ({
  page,
  court,
  setCourt,
  tabCategory,
  calendarData,
  curCalendarData,
  selectedDate,
  setSelectedDate,
}: IProps) => {
  const navigate = useNavigate();
  const { modalDispatch } = useModal();
  const [userGrade, setUserGrade] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserGrade = async () => {
      if (!isLogin()) return;
      try {
        const grade = await userGradeType();
        setUserGrade(grade);
      } catch (error) {
        console.error('유저 등급 정보 가져오기 실패:', error);
      }
    };

    fetchUserGrade();
  }, []);

  const [statusData, setStatusData] = useState([
    { state: '유찰', cnt: 0 },
    { state: '매각', cnt: 0 },
    { state: '변경', cnt: 0 },
    { state: '취하', cnt: 0 },
    { state: '기각', cnt: 0 },
    { state: '정지 및 기타', cnt: 0 },
    { state: '총경매수', cnt: 0 },
  ]);

  const getStatusData = async (selectedDate: string, court: string) => {
    try {
      const res = await LoanInfoService.calenderStatus(selectedDate, court);
      if (res && Array.isArray(res.data)) {
        setStatusData((prev) =>
          prev.map((item) => {
            const found = res.data.find(
              (data: any) => data.state === item.state,
            );
            return { ...item, cnt: found ? Number(found.cnt) : 0 };
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedDate === '' && court === '') return;
    getStatusData(selectedDate, court);
  }, [selectedDate, court]);

  const legend = [
    { name: '오늘', color: '#008AFF' },
    { name: '휴일', color: '#FA4032' },
    {
      name: tabCategory
        ? tabCategory === 'a'
          ? '진행건수'
          : tabCategory === 'n'
            ? '신건수'
            : '매각건수'
        : '신건수',
      color: 'rgba(55, 56, 60, 0.28)',
    },
  ];

  const getFormattedDate = (date: Date) => {
    return date
      .toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\. /g, '-')
      .replace('.', '');
  };

  const getTileContent = ({ date }: { date: Date }) => {
    const dateString = getFormattedDate(date);
    const count = calendarData?.find((item) => item.b_date === dateString)?.cnt;

    return count ? <div className="calender-count">({count})</div> : null;
  };

  const handleMonthChange = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = Number(selectedDate.split('-')[2]); // 현재 선택된 날짜의 일(day)

    // 새로운 날짜 생성 (현재 선택된 날짜의 일을 유지)
    const newDate = new Date(year, month, day);
    // 만약 해당 월에 그 날짜가 없으면 마지막 날짜로 설정
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    if (day > lastDayOfMonth) {
      newDate.setDate(lastDayOfMonth);
    }

    setSelectedDate(getFormattedDate(newDate));
  };

  const handleTileClick = (date: Date) => {
    setShowAll(false);
    if (page !== 'main') {
      setCourt('');
    }
    setSelectedDate(getFormattedDate(date));
  };

  const today = new Date()
    .toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\. /g, '-')
    .replace('.', '');

  const handleCourtClick = (courtname: string) => {
    if (!isLogin()) {
      modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
      return;
    }
    if (page === 'main') {
      if (tabCategory === 'a') {
        navigate(
          `/auctionSchedule?type=c&date=${selectedDate}&court=${courtname}`,
        );
      } else if (tabCategory === 'n') {
        navigate(`/NewAuction?type=c&date=${selectedDate}&court=${courtname}`);
      } else {
        localStorage.setItem('tab', 'search');
        navigate(
          `/auctionSchedule?type=c&date=${selectedDate}&court=${courtname}`,
        );
      }
    } else {
      setCourt(courtname);
    }
  };

  const [showAll, setShowAll] = useState(false);
  const [calendarViewMonth, setCalendarViewMonth] = useState(
    new Date(selectedDate).getMonth(),
  );

  return (
    <Container>
      <AuctionCalendarBox>
        <Calendar
          locale="ko-KR"
          value={new Date(selectedDate)}
          activeStartDate={new Date(selectedDate)}
          calendarType="gregory"
          tileContent={getTileContent}
          tileClassName={({ date }) => {
            const dateString = getFormattedDate(date);
            const day = date.getDay();
            const baseClass =
              day === 0 ? 'sunday' : day === 6 ? 'saturday' : '';
            return dateString === selectedDate
              ? `${baseClass} selected`
              : baseClass;
          }}
          onActiveStartDateChange={({ activeStartDate }) => {
            // 달 이동 막음
            const nextMonth = activeStartDate?.getMonth();
            if (nextMonth === calendarViewMonth) return;

            if (!isLogin()) {
              modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
              return;
            }
            if (!userGrade) {
              modalDispatch({ type: 'USER_ALERT', payload: 'free' });

              return;
            }
            if (activeStartDate && nextMonth) {
              setCalendarViewMonth(nextMonth);
              handleMonthChange(activeStartDate);
            }
          }}
          onClickDay={handleTileClick}
          prevLabel={
            <img
              src="/assets/images/arrow/arrow_right_24.svg"
              alt="Previous Month"
              style={{ transform: 'rotate(180deg)' }}
            />
          }
          nextLabel={
            <img
              src="/assets/images/arrow/arrow_right_24.svg"
              alt="Next Month"
            />
          }
        />

        <AuctionCalendarLegend>
          {legend.map((item, index) => (
            <AuctionCalendarLegendItem key={index}>
              <Circle style={{ backgroundColor: item.color }}></Circle>
              <span style={{ color: item.color }}>{item.name}</span>
            </AuctionCalendarLegendItem>
          ))}
        </AuctionCalendarLegend>
      </AuctionCalendarBox>
      <RightSeletedInfoBox>
        {/* <DateText>
          <span>{selectedDate}</span>
          {selectedDate === today && <Today>(오늘)</Today>}
        </DateText> */}
        <CourtList>
          {curCalendarData.length > 0 && (
            <CourtItem
              style={{
                color: court === '' ? '#008AFF' : '#616161',
              }}
              onClick={() => {
                page === 'main' ? handleCourtClick('') : setCourt('');
              }}
            >
              <div>법원전체</div>
              <div>
                (
                {calendarData.find((item) => item.b_date === selectedDate)?.cnt}
                )
              </div>
            </CourtItem>
          )}
          {curCalendarData &&
            curCalendarData
              .slice(0, showAll ? curCalendarData.length : 7)
              .map((item, index) => (
                <CourtItem
                  key={index}
                  style={{
                    color: item.courtname === court ? '#008AFF' : '#616161',
                  }}
                  onClick={() => {
                    handleCourtClick(item.courtname);
                  }}
                >
                  {ConvertCourtName(item.courtname_abbr)} ({item.cnt})
                </CourtItem>
              ))}
          {curCalendarData && curCalendarData.length === 0 && (
            <Nodata>
              {selectedDate === today ? (
                <>
                  <div>경매 개찰 중입니다.</div>
                  <div>개찰 완료 후 결과를 확인할 수 있습니다.</div>
                </>
              ) : selectedDate > today ? (
                <>
                  <div>경매 개찰 전입니다.</div>
                  <div>매각일 이후 결과를 확인해주세요.</div>
                </>
              ) : (
                '경매 일정이 없습니다.'
              )}
            </Nodata>
          )}
        </CourtList>
        <AutionStatusBox>
          {!isLogin() ? (
            <div
              style={{
                textAlign: 'center',
                fontSize: '13px',
                color: '#616161',
                fontWeight: '500',
                padding: '16px',
              }}
            >
              로그인 후 이용이 가능합니다.
            </div>
          ) : (
            statusData.map((item, index) => (
              <StatusItem
                key={index}
                style={{
                  borderBottom:
                    item.state === '정지 및 기타'
                      ? '1px solid #e1e2e4'
                      : 'none',
                  paddingBottom: item.state === '정지 및 기타' ? '8px' : '0px',
                }}
              >
                <span>{item.state}</span>
                <Count>
                  <CountNumber
                    style={{
                      color: item.state === '총경매수' ? '#008AFF' : '#222',
                    }}
                  >
                    {item.cnt}
                  </CountNumber>
                  <span>건</span>
                </Count>
              </StatusItem>
            ))
          )}
        </AutionStatusBox>
        {curCalendarData.length > 7 && (
          <MoreButton
            onClick={() => {
              setShowAll((prev) => !prev);
            }}
          >
            <div>{!showAll ? '더보기' : '접기'}</div>
            <img
              style={{
                transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              src="/assets/images/arrow/Arrow_down.png"
              alt=""
            />
          </MoreButton>
        )}
      </RightSeletedInfoBox>
    </Container>
  );
};

export default AuctionCalendar;

const ConvertCourtName = (courtname: string) => {
  if (courtname === '전주 본원') {
    return '전주법원';
  } else if (courtname.startsWith('서울')) {
    return courtname.slice(0, 4); // 0~4번째 글자 추출
  }
  return courtname;
};

const MoreButton = styled.div`
  width: 100%;
  color: black;
  font-size: 13px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 400;
  line-height: 18.85px;
  word-wrap: break-word;
  border: 1px #e1e2e4 solid;
  border-radius: 4px;
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  // gap: 40px;
  align-items: flex-start;
  // padding: 40px 60px;
  border-radius: 0px 0px 24px 24px;
  // border: 1px solid rgba(112, 115, 124, 0.16);
  background-color: #fff;
`;

const AuctionCalendarBox = styled.div`
  display: flex;
  flex-direction: column;
  //gap: 16px;
`;
const AuctionCalendarLegend = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const AuctionCalendarLegendItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-weight: 500;
  color: #008aff;
  margin-bottom: 16px;
`;
const Circle = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #008aff;
`;

const RightSeletedInfoBox = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100%;
`;
const DateText = styled.div`
  width: 100%;
  font-family: Spoqa Han Sans Neo;
  font-size: 24px;
  font-weight: 700;
  color: #222222;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(112, 115, 124, 0.22);
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;
const CourtList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 0;
  row-gap: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(112, 115, 124, 0.22);
`;
const CourtItem = styled.div`
  width: 25%;
  font-family: Spoqa Han Sans Neo;
  font-size: 11px;
  font-weight: 500;
  line-height: 17.4px;
  color: #616161;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: 1px;
  white-space: nowrap;
`;

const Today = styled.span`
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-weight: 500;
  color: rgba(55, 56, 60, 0.61);
`;

const Nodata = styled.div`
  width: 100%;
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-weight: 500;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AutionStatusBox = styled.div`
  width: 100%;
  padding: 16px;
  border-radius: 16px;
  background-color: #f7f8fc;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const StatusItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #616161;
`;
const Count = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
  font-size: 12px;
  font-weight: 500;
  color: #222;
`;
const CountNumber = styled.span`
  font-size: 14px;
  font-weight: 700;
`;
